/**
 * Which face each SEAT wears at one sitting — not which face a name wears in general.
 *
 * ========================= PHASE 6, BOX 0(3) ===================================
 * The Phase 5 fixture put "Amos" in the hero's chair and *Amos Creed* in the reveal:
 * the player had picked the marshal as their persona and the engine had dealt the
 * marshal a seat. Two identical portraits at one table, one of them the player, is a
 * picture nobody can read — and it is the one collision `roster.ts` cannot prevent,
 * because the roster is a constant map from a NAME to a FACE and the persona is a
 * face chosen without a name.
 *
 * ## What could not be done, and why this is the boundary that is left
 *
 * The Phase 5 review asked for the clashing BOT to be swapped out "before the `Game`
 * is built". It cannot be, and the reason is a rule this overhaul does not bend: the
 * cast is drawn inside `Game`'s constructor by `castFor(seats, seed)` from the session
 * seed's own stream (`engine/cast.ts`), and `app/engine/` is not touched. There is no
 * client-side lever that changes WHO is dealt a seat without changing the seed, and
 * changing the seed changes the cards.
 *
 * So the swap is of the FACE, not of the person. The seat keeps the name the engine
 * gave it — that name is on the wire, in the hand history and in the reveal, and it
 * must stay the same in all three — and this hands it a different portrait for the
 * length of the sitting. From the player's side the two are indistinguishable: they
 * never saw a face they could name, only a face that was theirs.
 *
 * ## The rule
 *
 * 1. Every seat starts on the roster's own slug for its name.
 * 2. A seat holding the hero's persona slug gives it up. So does the second of two
 *    seats holding the same slug — `agnew` and `bosley` both wear `dealer-m`, which
 *    `roster.ts` flags and nine-handed poker can seat together.
 * 3. A seat that gives up its slug takes the next unused one of the same TIER, in
 *    roster order, wrapping once. The tier is the slug's presentation family (`-m` or
 *    `-f`): `roster.ts` says in as many words that putting a man on `dealer-f` "would
 *    read as a bug rather than as casting", and that is the whole of the constraint.
 * 4. Nothing is ever taken from the hero, and no slug is used twice.
 *
 * Deterministic, pure, and a function of the seated names plus one string — so the
 * same sitting always dresses the same way and a replay looks identical.
 * ===============================================================================
 */
import { ROSTER, rosterForName, avatarUrl, type Expression } from "./roster";
import standingData from "./standing.json";

/** Male- or female-presenting, read off the slug the way the roster names them. */
export type Tier = "m" | "f";

export function tierOf(slug: string): Tier {
  return slug.endsWith("-f") ? "f" : "m";
}

/** Every slug the roster ships, once each, in roster order. */
const ALL_SLUGS: readonly string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of ROSTER) if (!seen.has(r.slug)) { seen.add(r.slug); out.push(r.slug); }
  return out;
})();

/**
 * Seat index -> slug, for the seats this sitting actually has.
 *
 * `names[i]` is the seat's name as the engine gave it; the hero's entry is skipped
 * (their face is the persona and is resolved elsewhere). A name the roster does not
 * know — the harness and the stub API both invent seats — is simply absent from the
 * result, and the caller falls back to the silhouette exactly as it did before.
 */
export function castFaces(
  names: readonly string[], heroSeat: number, persona: string | null,
): Record<number, string> {
  const taken = new Set<string>();
  if (persona) taken.add(persona);

  /* First pass: everyone who can keep their own face keeps it. */
  const out: Record<number, string> = {};
  const needs: number[] = [];
  for (let i = 0; i < names.length; i++) {
    if (i === heroSeat) continue;
    const entry = rosterForName(names[i] ?? "");
    if (!entry) continue;
    if (taken.has(entry.slug)) { needs.push(i); continue; }
    taken.add(entry.slug);
    out[i] = entry.slug;
  }

  /* Second pass: the ones who gave theirs up take the next unused of their tier. */
  for (const i of needs) {
    const own = rosterForName(names[i] ?? "")!.slug;
    const tier = tierOf(own);
    const from = Math.max(0, ALL_SLUGS.indexOf(own));
    let picked: string | null = null;
    for (let k = 1; k <= ALL_SLUGS.length && !picked; k++) {
      const s = ALL_SLUGS[(from + k) % ALL_SLUGS.length]!;
      if (tierOf(s) === tier && !taken.has(s)) picked = s;
    }
    /* A tier with nothing left over: take any unused face rather than none. Nine
       seats out of twenty-six slugs cannot reach this; a later, bigger table might. */
    if (!picked) picked = ALL_SLUGS.find((s) => !taken.has(s)) ?? null;
    if (picked) { taken.add(picked); out[i] = picked; }
  }
  return out;
}

/** The URL for a seat, given the sitting's own casting. Null draws the silhouette. */
export function seatAvatar(
  faces: Record<number, string> | undefined, seat: number, name: string,
  expression: Expression = "neutral", size: 128 | 512 = 128,
): string | null {
  const slug = faces?.[seat];
  if (slug) return avatarUrl(slug, expression, size);
  const entry = rosterForName(name);
  return entry ? avatarUrl(entry.slug, expression, size) : null;
}

/* ============================== PHASE 7: STANDING ================================ */


/**
 * The two keyed turnaround poses. The saloon stands everybody in THREE-QUARTER: Tim
 * ruled out the profile on the first renders (10 Sep, "can't even see their face"). The
 * profiles are still keyed and on the cast sheet, for the day a pose needs one.
 */
export type Pose = "three-quarter" | "profile";

/** What the saloon needs to stand a character up. */
export interface StandingCrop {
  url: string;
  /** Width ÷ height of the keyed crop. */
  aspect: number;
  /** The face, as fractions of the crop — the bubble's anchor and a hard obstacle. */
  face: { x: number; y: number; w: number; h: number };
  /** Which way the crop looks. The seat mirrors it so everybody faces the middle. */
  facing: "l" | "r";
}

interface KeyedEntry {
  slug: string; pose: Pose; aspect: number;
  face: StandingCrop["face"]; facing: "l" | "r"; status: "ok" | "defect"; note?: string;
}
const KEYED = (standingData as unknown as { crops: Record<string, KeyedEntry> }).crops;

/** The keyed crop's URL, whether or not it is fit to stand up. */
export function standingUrl(slug: string, pose: Pose): string {
  return `/art/characters/${slug}-${pose}.webp`;
}

/**
 * A character standing, or null — and null is THE FALLBACK RULE.
 *
 * The saloon stands each seat's character behind its place at the rail. A crop that is
 * missing, or that `key-cast.py` marked `defect` (the keyer chewed a shoulder, a hat,
 * a head — the list is in `standing.json` and in the Phase 7 report for Tim's
 * re-render), is not stood up: the seat falls back to its portrait disc on a rail
 * plate. A chewed silhouette standing at the table is worse than an honest disc.
 *
 * No fallback to the OTHER pose, on purpose: a profile in a three-quarter seat looks
 * away from the table, which reads as a mistake rather than as a choice.
 */
export function standing(slug: string | null | undefined, pose: Pose): StandingCrop | null {
  if (!slug) return null;
  const k = KEYED[`${slug}-${pose}`];
  if (!k || k.status !== "ok") return null;
  return { url: standingUrl(slug, pose), aspect: k.aspect, face: k.face, facing: k.facing };
}

/** The crops the keyer could not do cleanly, with what it did. For the kit's cast page. */
export function standingDefects(): { name: string; note: string }[] {
  return Object.entries(KEYED)
    .filter(([, k]) => k.status !== "ok")
    .map(([name, k]) => ({ name, note: k.note ?? "" }));
}

/** Every keyed crop, for the kit's cast page. */
export function standingAll(): (KeyedEntry & { name: string; url: string })[] {
  return Object.entries(KEYED).map(([name, k]) => ({ ...k, name, url: standingUrl(k.slug, k.pose) }));
}

/** The slug a seat is wearing this sitting: the cast face map, else the roster's own. */
export function seatSlug(faces: Record<number, string> | undefined, seat: number, name: string): string | null {
  return faces?.[seat] ?? rosterForName(name)?.slug ?? null;
}
